import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useDeadlineModal, {
  DeadlineModal,
} from "../../../hooks/useDeadlineModal";
import { auth } from "../../../Services/firebase-config";
import { useEvents } from "../../../Contexts/EventsContext";
import {
  getDeadlineTimestamp,
  calculateCountdown,
  formatDeadlineDisplay,
  hasDeadlinePassed,
} from "../../helpers/helpers";

const HeroSection = () => {
  const navigate = useNavigate();
  const { events } = useEvents();
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Get the first hackathon event from context
  const currentEvent = events.find((event) => event.eventType === "hackathon");

  const { isOpen, openModalForEvent, closeModal } = useDeadlineModal();

  // Get deadline timestamp using helper function
  const deadlineTimestamp = getDeadlineTimestamp(currentEvent?.deadline);

  // Calculate countdown to deadline
  useEffect(() => {
    if (!deadlineTimestamp) return;

    const updateCountdown = () => {
      setCountdown(calculateCountdown(currentEvent?.deadline));
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [deadlineTimestamp, currentEvent?.deadline]);

  // Calculate event status
  const getEventStatus = (event) => {
    const now = Date.now();
    const startDate = new Date(event.startDate).getTime();

    if (now > startDate) {
      return "past";
    } else if (deadlineTimestamp && now <= deadlineTimestamp) {
      return "open";
    } else {
      return "upcoming";
    }
  };

  // Registration is active if event has a deadline
  const isRegisterActive = currentEvent?.eventType === 'hackathon' ? !!currentEvent?.deadline : true;

  // Handle register button click
  const handleRegisterClick = () => {
    if (!isRegisterActive) return;

    if (currentEvent.eventType === 'grant') {
      navigate('/');
    } else if (currentEvent.eventType === 'hackathon') {
      // Check if deadline has passed using helper
      if (hasDeadlinePassed(currentEvent?.deadline)) {
        // If user is not authenticated, show deadline modal
        if (!auth.currentUser) {
          openModalForEvent(currentEvent);
          return;
        }
        // If user is authenticated, redirect to dashboard
        navigate(`/my-events`, { state: { isEvent: true, eventId: currentEvent.id } });
        return;
      }

      // Deadline hasn't passed - continue with registration or dashboard
      if (auth.currentUser) {
        // User is logged in, redirect to dashboard
        navigate(`/my-events`, { state: { isEvent: true, eventId: currentEvent.id } });
      } else {
        // User is not logged in, redirect to registration
        navigate(`/register`, { state: { isEvent: true, eventId: currentEvent.id } });
      }
    } else {
      console.log('RSVP functionality to be implemented');
    }
  };

  if (!currentEvent) {
    return null; // Return null if no hackathon event available
  }

  return (
    <section className="bg-custom-blue-1 text-white relative min-h-screen lg:h-[100vh]">
      <div className="container mx-auto px-2 sm:px-3 pt-20 sm:pt-16 pb-6 lg:pb-0 relative z-10 h-full flex items-center">
        <div className="flex flex-wrap items-center w-full">
          <div className="w-full lg:w-7/12 mb-6 lg:mb-0 lg:pr-6 sm:mt-8 lg:mt-0">
            <div className="inline-block px-2 sm:px-3 py-1 text-xs font-semibold text-white bg-custom-green-1 rounded-full mb-3 sm:mb-4">
              <span className="hidden sm:inline">FEATURED HACKATHON • </span>
              {getEventStatus(currentEvent) === "open"
                ? "APPLICATIONS OPEN"
                : "UPCOMING EVENT"}
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 leading-tight">
              {currentEvent.title}
            </h1>
            <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-3 sm:mb-4 text-custom-green-1 leading-tight">
              {currentEvent.date}
            </h2>
            <div className="mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm md:text-base text-gray-200 font-medium leading-relaxed">
                {currentEvent.description}
              </p>
            </div>
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-gray-300">
                <span className="flex items-center gap-1">
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm">
                    {currentEvent.location}
                  </span>
                </span>
                {(currentEvent.prizes || currentEvent.funding) && (
                  <span className="flex items-center gap-1">
                    <span className="text-xs sm:text-sm">
                      {currentEvent.prizes || currentEvent.funding}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="mb-4 sm:mb-6">
              <h3 className="text-sm sm:text-base font-bold text-white mb-1 sm:mb-2">
                Registration Deadline
              </h3>
              <p className="text-[10px] sm:text-xs text-white/80 mb-2">
                Register your account before the deadline to create teams and
                submit your application.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 mb-2 sm:mb-3">
                <div className="bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg p-1 sm:p-2 text-center">
                  <div className="text-base sm:text-lg font-bold text-custom-green-1">
                    {countdown.days}
                  </div>
                  <div className="text-[9px] sm:text-xs text-white/80 uppercase tracking-wide">
                    Days
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg p-1 sm:p-2 text-center">
                  <div className="text-base sm:text-lg font-bold text-custom-green-1">
                    {countdown.hours}
                  </div>
                  <div className="text-[9px] sm:text-xs text-white/80 uppercase tracking-wide">
                    Hours
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg p-1 sm:p-2 text-center">
                  <div className="text-base sm:text-lg font-bold text-custom-green-1">
                    {countdown.minutes}
                  </div>
                  <div className="text-[9px] sm:text-xs text-white/80 uppercase tracking-wide">
                    Minutes
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg p-1 sm:p-2 text-center">
                  <div className="text-base sm:text-lg font-bold text-custom-green-1">
                    {countdown.seconds}
                  </div>
                  <div className="text-[9px] sm:text-xs text-white/80 uppercase tracking-wide">
                    Seconds
                  </div>
                </div>
              </div>
              <p className="text-[10px] sm:text-xs text-white/90">
                Deadline: {formatDeadlineDisplay(currentEvent?.deadline)}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleRegisterClick}
                disabled={!isRegisterActive}
                className={`w-full sm:w-auto px-4 py-2 bg-custom-green-1 text-white font-bold rounded-lg hover:bg-opacity-90 transition transform hover:scale-105 shadow-lg text-center disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {currentEvent.eventType === "hackathon" ||
                currentEvent.eventType === "grant"
                  ? "Apply Now"
                  : "RSVP"}
              </button>{" "}
              <button
                className="px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-custom-blue-1 transition"
                onClick={() => navigate(`/${currentEvent.id}`)}
              >
                View Details
              </button>
            </div>
          </div>
          <div className="w-full lg:w-5/12 mt-6 lg:mt-0 relative lg:pl-8">
            <div className="p-1 sm:p-2 rounded-xl">
              <div className="aspect-square sm:aspect-[4/3] lg:aspect-square rounded-xl overflow-hidden relative bg-white/5 backdrop-blur-sm border border-white/10">
                <div className="absolute inset-0 z-10"></div>
                <img
                  src={currentEvent.image || "/Images/background.jpg"}
                  alt={currentEvent.title}
                  className="absolute inset-0 w-full h-full object-cover object-center opacity-30 z-0"
                />
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="text-center p-2 sm:p-3 lg:p-6">
                    <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 sm:mb-2">
                      {currentEvent.title}
                    </h3>
                    <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                      {currentEvent.tags.slice(0, 4).map((tag, index) => (
                        <span
                          key={index}
                          className="px-1 sm:px-2 py-[2px] sm:py-1 bg-white/20 text-white text-[9px] sm:text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeadlineModal
        isOpen={isOpen}
        event={currentEvent}
        deadline={formatDeadlineDisplay()}
        onClose={closeModal}
      />
    </section>
  );
};

export default HeroSection;
